/**
 * Друкована форма «Акт повернення товару» (рішення власника 2026-07-28).
 *
 * Двосторонній акт довільної форми — для випадків, коли покупець не дає
 * власну накладну на повернення (фізособа/неплатник): Покупець повернув,
 * Продавець прийняв; підстава — видаткова накладна реалізації; таблиця
 * товарів, суми, пропис, підписи сторін. Єдиний стиль друку проекту
 * (print-page + print-mode, кнопки «Друк / PDF» / «Назад»).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { goodsReturnsFromCustomer } from '@/api/goodsReturns';
import { goodsSales } from '@/api/goodsSales';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { amountToWordsUA } from '@/utils/numberToWordsUA';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsReturnFromCustomerPrintPage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['grfc', id],
    queryFn: () => goodsReturnsFromCustomer.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;

  const saleQ = useQuery({
    queryKey: ['goods-sale', d?.base_sale_id],
    queryFn: () => goodsSales.get(d!.base_sale_id!),
    enabled: !!d?.base_sale_id,
  });
  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const buyer = contractorsQ.data?.items.find((c) => c.id === d?.contractor_id);
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження акта…</div>;
  }

  const nomById = new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n]));
  const uomById = new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name]));
  const totalNoVat = d.items.reduce((s, it) => s + Number(it.amount_without_vat), 0);
  const totalVat = d.items.reduce((s, it) => s + Number(it.vat_amount), 0);
  // ч.1381: ПДВ у рядках — документ з ПДВ, навіть якщо організація тепер не платник.
  const isVatPayer = org.is_vat_payer || totalVat > 0;
  const total = totalNoVat + totalVat;
  const sale = saleQ.data;

  return (
    <div className="print-page invoice-print" style={{
      maxWidth: 800, margin: '0 auto', padding: '1.5rem',
      fontFamily: "'Times New Roman', serif", fontSize: '10.5pt', background: 'var(--bg-panel)',
    }}>
      <style>{`
        .ret-table { width: 100%; border-collapse: collapse; margin: 0.5rem 0; font-size: 9pt; line-height: 1.15; }
        .ret-table th, .ret-table td { border: 1px solid #000; padding: 1px 4px; }
        .ret-table th { text-align: center; font-weight: 600; font-size: 8pt; background: #f5f5f5; text-transform: none; letter-spacing: 0; }
      `}</style>

      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.goods_returns_from_customer',
        documentId: d.id,
        filename: `Повернення від покупця ${d.document_number}`,
        orgName: org.name,
      }} />

      <h2 style={{ textAlign: 'center', margin: '0.75rem 0' }}>
        Акт повернення товару № {d.document_number}
        {' '}від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </h2>

      <table style={{ width: '100%', fontSize: '10.5pt', marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ width: 110, verticalAlign: 'top', color: '#333' }}>Продавець:</td>
            <td>
              <strong>{org.full_name || org.name}</strong><br />
              {partyCodesText(org) && <>{partyCodesText(org)}{', '}</>}
              {org.legal_address ?? ''}
            </td>
          </tr>
          <tr>
            <td style={{ verticalAlign: 'top', color: '#333', paddingTop: 6 }}>Покупець:</td>
            <td style={{ paddingTop: 6 }}>
              <strong>{buyer?.name ?? '—'}</strong>
              {partyCodesText(buyer) && <>{', '}{partyCodesText(buyer)}</>}
              {buyer?.legal_address && <><br />{buyer.legal_address}</>}
            </td>
          </tr>
          {sale && (
            <tr>
              <td style={{ color: '#333', paddingTop: 6 }}>Підстава:</td>
              <td style={{ paddingTop: 6 }}>
                видаткова накладна № {sale.document_number} від{' '}
                {new Date(sale.document_date).toLocaleDateString('uk-UA')}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <p style={{ margin: '0.5rem 0' }}>
        Цим актом сторони підтверджують, що Покупець повернув, а Продавець
        прийняв такий товар:
      </p>

      <table className="ret-table">
        <thead>
          <tr>
            <th style={{ width: 30 }}>№</th>
            <th>Найменування товару</th>
            <th style={{ width: 70 }}>К-сть</th>
            <th style={{ width: 50 }}>Од.</th>
            <th style={{ width: 90 }}>{isVatPayer ? 'Ціна без ПДВ' : 'Ціна'}</th>
            <th style={{ width: 100 }}>{isVatPayer ? 'Сума без ПДВ' : 'Сума'}</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it, i) => {
            const nom = nomById.get(it.nomenclature_id);
            return (
              <tr key={i}>
                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                <td>{nom?.name ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {Number(it.quantity).toLocaleString('uk-UA', { maximumFractionDigits: 3 })}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {nom?.base_uom_id ? (uomById.get(nom.base_uom_id) ?? 'шт') : 'шт'}
                </td>
                <td style={{ textAlign: 'right' }}>{fmt(it.price)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(it.amount_without_vat)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <table style={{ width: '100%', fontSize: '11pt' }}>
        <tbody>
          <tr>
            <td></td>
            <td style={{ width: 220 }}>
              <table style={{ width: '100%' }}>
                <tbody>
                  {isVatPayer ? (
                    <>
                      <tr><td>Разом без ПДВ:</td>
                        <td style={{ textAlign: 'right' }}><strong>{fmt(totalNoVat)}</strong></td></tr>
                      <tr><td>ПДВ:</td>
                        <td style={{ textAlign: 'right' }}><strong>{fmt(totalVat)}</strong></td></tr>
                      <tr style={{ fontSize: '12pt' }}><td>Разом з ПДВ:</td>
                        <td style={{ textAlign: 'right' }}><strong>{fmt(total)}</strong></td></tr>
                    </>
                  ) : (
                    <tr style={{ fontSize: '12pt' }}><td>Разом (без ПДВ):</td>
                      <td style={{ textAlign: 'right' }}><strong>{fmt(total)}</strong></td></tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <p style={{ margin: '0.75rem 0' }}>
        Всього повернуто найменувань {d.items.length}, на суму{' '}
        <strong>{fmt(total)} грн</strong><br />
        <em>{amountToWordsUA(total)}</em>
      </p>

      <p style={{ margin: '0.5rem 0' }}>
        Товар повернуто у належному стані. Розрахунки за повернутий товар
        сторони проводять згідно з умовами договору. Сторони претензій одна
        до одної не мають.
      </p>

      <table style={{ width: '100%', marginTop: 28, fontSize: '10.5pt' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Повернув (Покупець)</strong><br /><br />
              _______________________
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б., М.П.</span>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <strong>Прийняв (Продавець)</strong><br /><br />
              _______________________
              {org.director_name && (
                <><br /><span style={{ color: '#333' }}>({org.director_name})</span></>
              )}
              <br /><span style={{ color: '#333', fontSize: '9pt' }}>підпис, П.І.Б., М.П.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
